/**
 * MapLayerControl — Google-Maps-optimized 3-group layer menu.
 *
 * Group 1: KARTENTYP (background tile — radio, pick ONE)
 * Group 2: EINFÄRBUNG (field coloring — radio, ONE at a time)
 * Group 3: ANZEIGEN (structure overlays — checkboxes, multiple)
 *
 * UX-P3: Karten-Layer-Menü sprint.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

// ── Layer configuration ─────────────────────────────────────────────────────

export const KARTENTYP_OPTIONS = [
  { id: 'osm', icon: '🗺️', labelKey: 'map.layers.osm' },
  { id: 'satellite', icon: '🛰️', labelKey: 'map.layers.satellite' },
];

export const EINFAERBUNG_OPTIONS = [
  { id: 'crop', icon: '🌱', labelKey: 'map.layers.crop' },
  { id: 'yield', icon: '📊', labelKey: 'map.layers.yield', badge: 'map.layers.yieldBadge' },
  { id: 'status', icon: '🛡️', labelKey: 'map.layers.status' },
  { id: 'spray', icon: '💨', labelKey: 'map.layers.spray' },
  { id: 'none', icon: '⬜', labelKey: 'map.layers.none' },
];

export const ANZEIGEN_OPTIONS = [
  { id: 'boundaries', icon: '📐', labelKey: 'map.layers.boundaries', featureKey: null },
  { id: 'pois', icon: '📍', labelKey: 'map.layers.pois', featureKey: null },
  { id: 'buildings', icon: '🏠', labelKey: 'map.layers.buildings', featureKey: 'buildings_layer' },
];

// Future gated layers — strictly hidden until data exists
// { id: 'tasks', labelKey: 'map.layers.tasks', featureKey: 'tasks_layer' },
// { id: 'machines', labelKey: 'map.layers.machines', featureKey: 'machines_layer' },
// { id: 'machine_tracks', labelKey: 'map.layers.machineTracks', featureKey: 'machine_tracks_layer' },
// { id: 'workers', labelKey: 'map.layers.workers', featureKey: 'workers_layer' },
// { id: 'weather_stations', labelKey: 'map.layers.weatherStations', featureKey: 'weather_stations_layer' },
// { id: 'yield_heatmap', labelKey: 'map.layers.yieldHeatmap', featureKey: 'yield_heatmap_layer' },

export default function MapLayerControl({
  tileSource,
  onTileSourceChange,
  coloring,
  onColoringChange,
  overlays,
  onOverlayToggle,
  poiCount,
  isFeatureEnabled,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const visibleAnzeigen = ANZEIGEN_OPTIONS.filter(
    opt => !opt.featureKey || isFeatureEnabled?.(opt.featureKey)
  );

  return (
    <div className="relative pointer-events-auto" data-testid="map-layer-control">
      {/* Toggle button */}
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg shadow text-[11px] font-medium transition-colors ${
          open
            ? 'bg-[var(--aif-color-primary)] text-white'
            : 'bg-[var(--aif-bg-surface)]/90 backdrop-blur-sm text-[var(--aif-text-secondary)] border border-[var(--aif-border-default)]'
        }`}
        aria-label={t('map.layers.toggle')}
        aria-expanded={open}
        data-testid="layer-control-toggle"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6.429 9.75L2.25 12l4.179 2.25m0-4.5l5.571 3 5.571-3m-11.142 0L2.25 7.5 12 2.25l9.75 5.25-4.179 2.25m0 0L12 12.75 6.429 9.75m11.142 0l4.179 2.25L12 17.25 2.25 12l4.179-2.25m11.142 0l4.179 2.25L12 22.5l-9.75-5.25 4.179-2.25" />
        </svg>
        {t('map.layers.toggle')}
      </button>

      {/* Dropdown panel */}
      {open && (
        <>
          <div className="fixed inset-0 z-[var(--aif-z-map-overlay)]" onClick={() => setOpen(false)} />
          <div
            className="absolute top-full right-0 mt-1.5 w-56 rounded-xl shadow-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] z-[var(--aif-z-contextual)] overflow-hidden"
            data-testid="layer-control-panel"
          >
            {/* Group 1: Kartentyp */}
            <div className="px-3 pt-3 pb-2">
              <div className="text-[10px] font-semibold text-[var(--aif-text-muted)] uppercase tracking-wide mb-1.5">
                {t('map.layers.kartentyp')}
              </div>
              <div className="flex gap-1.5">
                {KARTENTYP_OPTIONS.map(opt => (
                  <button
                    key={opt.id}
                    onClick={() => onTileSourceChange(opt.id)}
                    className={`flex-1 flex flex-col items-center gap-1 py-2 rounded-lg text-[10px] font-medium transition-colors ${
                      tileSource === opt.id
                        ? 'bg-[var(--aif-color-primary)]/10 text-[var(--aif-color-primary)] ring-1 ring-[var(--aif-color-primary)]'
                        : 'bg-[var(--aif-bg-elevated)] text-[var(--aif-text-secondary)] hover:bg-[var(--aif-bg-tertiary)]'
                    }`}
                    data-testid={`layer-tile-${opt.id}`}
                  >
                    <span className="text-lg">{opt.icon}</span>
                    <span>{t(opt.labelKey)}</span>
                  </button>
                ))}
              </div>
            </div>

            <hr className="border-[var(--aif-border-default)]" />

            {/* Group 2: Einfärbung */}
            <div className="px-3 py-2">
              <div className="text-[10px] font-semibold text-[var(--aif-text-muted)] uppercase tracking-wide mb-1.5">
                {t('map.layers.einfaerbung')}
              </div>
              <div className="space-y-0.5">
                {EINFAERBUNG_OPTIONS.map(opt => (
                  <button
                    key={opt.id}
                    onClick={() => onColoringChange(opt.id)}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-[11px] transition-colors ${
                      coloring === opt.id
                        ? 'bg-[var(--aif-color-primary)]/10 text-[var(--aif-color-primary)] font-medium'
                        : 'text-[var(--aif-text-secondary)] hover:bg-[var(--aif-bg-elevated)]'
                    }`}
                    data-testid={`layer-color-${opt.id}`}
                  >
                    <span className="w-4 h-4 flex items-center justify-center rounded-full border border-current flex-shrink-0">
                      {coloring === opt.id && <span className="w-2 h-2 rounded-full bg-current" />}
                    </span>
                    <span>{opt.icon}</span>
                    <span className="flex-1 text-left">{t(opt.labelKey)}</span>
                    {opt.badge && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-[var(--aif-bg-elevated)] text-[var(--aif-text-muted)]">
                        {t(opt.badge)}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <hr className="border-[var(--aif-border-default)]" />

            {/* Group 3: Anzeigen */}
            <div className="px-3 py-2 pb-3">
              <div className="text-[10px] font-semibold text-[var(--aif-text-muted)] uppercase tracking-wide mb-1.5">
                {t('map.layers.anzeigen')}
              </div>
              <div className="space-y-0.5">
                {visibleAnzeigen.map(opt => {
                  const checked = overlays.has(opt.id);
                  return (
                    <button
                      key={opt.id}
                      onClick={() => onOverlayToggle(opt.id)}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-[11px] transition-colors ${
                        checked
                          ? 'text-[var(--aif-text-primary)]'
                          : 'text-[var(--aif-text-muted)] hover:bg-[var(--aif-bg-elevated)]'
                      }`}
                      data-testid={`layer-overlay-${opt.id}`}
                    >
                      <span className={`w-4 h-4 flex items-center justify-center rounded border flex-shrink-0 transition-colors ${
                        checked
                          ? 'bg-[var(--aif-color-primary)] border-[var(--aif-color-primary)] text-white'
                          : 'border-[var(--aif-border-default)]'
                      }`}>
                        {checked && (
                          <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                        )}
                      </span>
                      <span>{opt.icon}</span>
                      <span className="flex-1 text-left">{t(opt.labelKey)}</span>
                      {opt.id === 'pois' && poiCount > 0 && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[var(--aif-bg-elevated)] text-[var(--aif-text-muted)]">
                          {poiCount}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
